/**
 * The pipeline's glance strip (pipelines.md 8.3), the shared StatStrip: four numbers, each with
 * the figure that explains it in one place and at one size (a 128 by 28 box at the right of the
 * number): in flight with its state bar, how the last items ended as a run strip, the median
 * time to land with its trend, and the worker pool as a thin bar that opens who holds it. Grey
 * by default; colour only for what runs or failed. A full pool is the machine at work, so it
 * keeps the working tone, never amber. An idle pool reads "30 free" with no bar.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Machine, Pipeline } from '@orgus/protocol';
import { MeterBar, RunStrip, Sparkline, StatStrip, StatTile, StateBar, formatDuration, formatInt, plural } from '@orgus/ui';
import { CapacityDrawer } from './Capacity.tsx';
import { capacityOwners, capacityView } from './model.ts';
import { pipelineFigures } from './present.ts';

/** Every figure in the strip: the same box, at the right of its number, centred on it. */
function Figure({ children }: { children: ReactNode }) {
  return <span className="hidden h-7 w-20 items-center sm:flex xl:w-32 [&>*]:w-full">{children}</span>;
}

/** The run strip's most slots: 20 results fill the figure, fewer draw only as wide as they are (no empty bars). */
const RUN_SLOTS = 20;

/** A sub line in two lengths: the short one on a phone, where a tile is half the width. */
function Sub({ short, long }: { short: string; long: string }) {
  if (short === long) return <>{long}</>;
  return (
    <>
      <span className="sm:hidden">{short}</span>
      <span className="max-sm:hidden">{long}</span>
    </>
  );
}

/** "18 tasks waiting for a worker" reads "18 waiting" on a phone. */
export function shortCapacity(detail: string): string {
  const m = /^(\d[\d,]*)\s+\w+\s+waiting\b/i.exec(detail.trim());
  return m ? `${m[1]} waiting` : detail;
}

export function PipelineFigures({ pipeline: p, machine, flight }: { pipeline: Pipeline; machine: Machine | undefined; flight: string }) {
  const f = useMemo(() => pipelineFigures(p), [p]);
  const [workers, setWorkers] = useState(false);
  const cap = p.capacity ? capacityView(p.capacity) : null;
  const failed = f.outcomes.filter((o) => o === 'failed').length;
  const busy = cap ? Math.min(cap.used, cap.total) : 0;
  const queued = f.inFlight.queued ?? 0;
  const moving = f.inFlightTotal - queued;
  const flightShort = moving && queued ? `${formatInt(moving)} active, ${formatInt(queued)} queued` : flight;
  const runs = f.outcomes.slice(-RUN_SLOTS);
  return (
    <>
      <StatStrip label="Pipeline figures" className="lg:grid-cols-4">
        <StatTile
          label="In flight"
          value={f.inFlightTotal}
          sub={f.inFlightTotal ? <Sub short={flightShort} long={flight} /> : 'Nothing waiting'}
          trend={
            f.inFlightTotal ? (
              <Figure>
                <StateBar counts={f.inFlight} label={flight} />
              </Figure>
            ) : undefined
          }
        />
        <StatTile
          label="Recent results"
          value={f.passed}
          unit="landed"
          sub={f.outcomes.length ? (failed ? `${formatInt(failed)} failed of the last ${formatInt(f.outcomes.length)}` : `All ${formatInt(f.outcomes.length)} passed`) : 'Nothing finished yet'}
          trend={
            runs.length ? (
              <Figure>
                {/* Only the results there are, at the strip's right: never a row of empty bars before them. */}
                <span className="flex justify-end">
                  <span className="flex" style={{ width: `${(runs.length / RUN_SLOTS) * 100}%` }}>
                    <RunStrip runs={runs} slots={runs.length} passed="success" label={`Last ${formatInt(runs.length)}: ${formatInt(f.passed)} passed, ${formatInt(failed)} failed`} className="w-full" />
                  </span>
                </span>
              </Figure>
            ) : undefined
          }
        />
        <StatTile
          label="Time to land"
          featured
          value={f.medianMs ?? 0}
          format={(ms) => (f.medianMs == null ? 'None yet' : formatDuration(ms >= 60_000 ? Math.round(ms / 60_000) * 60_000 : Math.round(ms / 1000) * 1000))}
          sub={f.durations.length ? <Sub short={`Median, last ${formatInt(f.durations.length)}`} long={`Median of the last ${plural(f.durations.length, 'landing')}`} /> : 'Shows once something lands'}
          trend={
            f.durations.length > 1 ? (
              <Figure>
                <Sparkline values={f.durations} width={128} height={28} min={0} slot={2} label={`Time to land, oldest first: ${f.durations.map((d) => formatDuration(d)).join(', ')}`} />
              </Figure>
            ) : undefined
          }
        />
        {cap && p.capacity ? (
          <StatTile
            label={p.capacity.label}
            // Nothing running: the pool reads as what is free, not as an empty meter.
            value={busy ? busy : cap.total}
            of={busy ? cap.total : undefined}
            unit={busy ? undefined : 'free'}
            sub={busy ? <Sub short={shortCapacity(cap.detail)} long={cap.detail} /> : p.capacity.runs ? cap.summary : 'Nothing running'}
            // A busy machine is work, not a warning: the pool reads in the working tone, never amber.
            trend={
              busy ? (
                <Figure>
                  <MeterBar value={cap.fraction} tone="working" label={cap.summary} />
                </Figure>
              ) : undefined
            }
            onOpen={() => setWorkers(true)}
          />
        ) : (
          <StatTile label="Failed" value={failed} sub={failed ? 'In the recent results' : 'Nothing failed recently'} />
        )}
      </StatStrip>
      {p.capacity && <CapacityDrawer open={workers} onOpenChange={setWorkers} capacity={p.capacity} machineName={machine?.name ?? 'this machine'} reserveNote={capacityOwners(p).reserveNote} owners={capacityOwners(p)} />}
    </>
  );
}
